import { Injectable } from '@angular/core';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { MessageGroupModel } from './message-group.model';
import { ChatMessageBase } from 'common/xislivechat/models/chat-message-base';
import { WorkItemTypes } from 'common/workitems/constants/workitem-types';
import { TextChatMessage } from 'common/xislivechat/models/text-chat-message';
import { DiscussionOperatorTypes, DiscussionsMessagesTypes } from 'shared/components/discussions/discussion-properties';

@Injectable()
export class MessageGroupService {
  groupMessagesByTypeAndUser(messages: MessageGroupModel[], workItemType): MessageGroupModel[] {
    const groupConditionFn = (lastMessage, currentMessageType, currentMessage) => lastMessage ? (lastMessage.type === currentMessageType && lastMessage.personId === currentMessage.PersonID) : false;

    return this.groupMessages(messages, groupConditionFn, workItemType);
  }

  groupMessagesByType(messages: MessageGroupModel[], workItemType): MessageGroupModel[] {
    const groupConditionFn = (lastMessage, currentMessageType) => lastMessage ? lastMessage.type === currentMessageType : false;

    return this.groupMessages(messages, groupConditionFn, workItemType);
  }

  groupMessages(messages: MessageGroupModel[], groupConditionFn, workItemType): MessageGroupModel[] {
    const newMessages: MessageGroupModel[] = [];
    let lastMessage;
    let messageType;
    let messageGroupType;
    let tempMessages;
    let lastTypeMatch;

    messages.forEach((msg) => {
      // Clear last message
      lastMessage = undefined;

      // Get the last message
      if (newMessages.length > 0) {
        lastMessage = newMessages[newMessages.length - 1];
      }

      // Get the type of the message
      if (workItemType === WorkItemTypes.DISCUSSION) {
        messageGroupType = this._getMessageGroupTypeForDiscussion(msg);
        messageType = messageGroupType;
      } else if (workItemType === WorkItemTypes.CHAT) {
        messageType = this._getMessageGroupTypeForChat(msg);
      } else {
        messageType = this._getMessageGroupTypeForTextChat(msg);
      }

      // Check if last message match the current message based on the given condition
      lastTypeMatch = groupConditionFn(lastMessage, messageType, msg);
      if (lastTypeMatch) {
        lastMessage.messages.push({ ...msg });
      } else {
        tempMessages = [];
        tempMessages.push({ ...msg });
        newMessages.push({ type: messageType, personId: msg.PersonID, messages: tempMessages });
      }
    });

    return newMessages;
  }

  addNewMessagesToGroups(currentGroups: MessageGroupModel[], messages: MessageGroupModel[], workItemType): MessageGroupModel[] {
    let newGroups;
    if (workItemType === WorkItemTypes.DISCUSSION) {
      newGroups = this.groupMessagesByType(messages, workItemType);
    } else {
      newGroups = this.groupMessagesByTypeAndUser(messages, workItemType);
    }
    return this.addNewItemsToList(currentGroups, newGroups);
  }

  addNewItemsToList(currentGroups: MessageGroupModel[], newGroups: MessageGroupModel[]): MessageGroupModel[] {
    const newLength = newGroups.length;
    const currentLength = currentGroups.length;
    if (currentLength !== 0 || newLength !== 0) {
      if (currentLength <= newLength) {
        this.updateLastGroupMessages(currentGroups, newGroups);
        this.copyItemsFromListToList(currentGroups, newGroups, currentGroups.length, newLength);
      } else {
        // simply drop current groups, we start from the beginning
        return newGroups;
      }
    }

    return currentGroups;
  }

  updateLastGroupMessages(currentGroups: MessageGroupModel[], newGroups: MessageGroupModel[]) {
    if (currentGroups.length > 0) {
      const lastKnownGroupIndex = currentGroups.length - 1;
      const lastKnownGroup = currentGroups[lastKnownGroupIndex];
      const lastKnownGroupLastKnownMessageIndex = Math.max(0, lastKnownGroup.messages.length - 1);
      this.copyItemsFromListToList(lastKnownGroup.messages, newGroups[lastKnownGroupIndex].messages, lastKnownGroupLastKnownMessageIndex, newGroups[lastKnownGroupIndex].messages.length);
    }
  }

  copyItemsFromListToList(destination, source, fromIndex, toIndex) {
    for (let i = fromIndex; i < toIndex; i++) {
      destination[i] = source[i];
    }
  }

  private _getMessageGroupTypeForDiscussion(message) {
    if (message.MessageType === DiscussionsMessagesTypes.SYSTEM) {
      return bcEnums.messageGroupTypes.SYSTEM;
    } else if (message.OperatorType === DiscussionOperatorTypes.OPERATOR) {
      return bcEnums.messageGroupTypes.OPERATOR;
    } else {
      return bcEnums.messageGroupTypes.HELPER;
    }
  }

  private _getMessageGroupTypeForChat(message) {
    if (ChatMessageBase.isVisitor(message)) {
      return bcEnums.messageTypes.VISITOR;
    } else if (ChatMessageBase.isAgent(message)) {
      return bcEnums.messageTypes.OPERATOR;
    } else if (ChatMessageBase.isSystem(message)) {
      return bcEnums.messageTypes.SYSTEM;
    }
  }

  private _getMessageGroupTypeForTextChat(message) {
    if (TextChatMessage.isVisitor(message)) {
      return bcEnums.messageTypes.VISITOR;
    } else if (TextChatMessage.isAgent(message)) {
      return bcEnums.messageTypes.OPERATOR;
    } else if (TextChatMessage.isSystem(message)) {
      return bcEnums.messageTypes.SYSTEM;
    }
  }
}
